/**
 * Dashboard charts live in an iframe, but createChart and its helpers (theming, label
 * enforcement, manual overrides) run in the parent window. Each window is its own JS realm with
 * its own `Object`, and Chart.js merges its defaults into a nested options object only when
 * `value.constructor === Object` in the iframe's realm. A `{}` written by parent code fails that
 * check, so Chart.js treats it as a finished value and merges nothing: a parent-made
 * `plugins.legend = {}` produced a legend with no label generator, zero items, and a blank card
 * when the legend sat left or right.
 *
 * This walks the finished config once, right before `new Chart`, and swaps every parent-made
 * plain object for an identical one created with the iframe's `Object`. Keys and values are
 * unchanged; only the objects' realm is. Iframe-made objects are walked in place so the render
 * JS keeps its references; arrays, functions and class instances pass through untouched.
 */
export function adoptChartRealm<T extends object>(config: T, targetWindow: Window): T {
  const TargetObject = (targetWindow as unknown as { Object?: ObjectConstructor }).Object;
  if (!TargetObject || TargetObject === Object) {
    return config;
  }
  const adopted = new WeakMap<object, unknown>();
  const adopt = (value: unknown): unknown => {
    if (typeof value !== "object" || value === null) {
      return value;
    }
    if (adopted.has(value)) {
      return adopted.get(value);
    }
    const proto = Object.getPrototypeOf(value);
    const isParentPlain = proto === Object.prototype;
    if (!Array.isArray(value) && !isParentPlain && proto !== TargetObject.prototype) {
      return value;
    }
    const target = (isParentPlain ? new TargetObject() : value) as Record<string, unknown>;
    adopted.set(value, target);
    for (const key of Object.keys(value)) {
      target[key] = adopt((value as Record<string, unknown>)[key]);
    }
    return target;
  };
  return adopt(config) as T;
}
